import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMediaQuery } from '../../../hooks';
import { CARDS_PER_PAGE_DESKTOP, CARDS_PER_PAGE_TABLET } from '../constants';

type UseMenuCarouselHandlerProps = {
  totalCards: number;
  cardsPerPageDesktop?: number;
  cardsPerPageTablet?: number;
};

/** MenuCarousel and MenuCarouselItem Hook.
 * Example of use:
 * `const { menuCarouselProps, menuItemProps } = useMenuCarouselHandler(totalCards);`
 * `cardsPerPageDesktop` and `cardsPerPageTablet` are optional, and may be included
 * if you need to alter de default number that it `4` for desktop and `3` for tablet
 */
export const useMenuCarouselHandler = ({
  totalCards,
  cardsPerPageDesktop,
  cardsPerPageTablet,
}: UseMenuCarouselHandlerProps) => {
  const isDesktop = useMediaQuery('desktopLarge');

  const cardsPerPage = useMemo(() => {
    return isDesktop
      ? cardsPerPageDesktop ?? CARDS_PER_PAGE_DESKTOP
      : cardsPerPageTablet ?? CARDS_PER_PAGE_TABLET;
  }, [isDesktop, cardsPerPageDesktop, cardsPerPageTablet]);

  const [itemOffset, setItemOffset] = useState(0);
  const [lastItemSlide, setLastSlideItem] = useState(cardsPerPage);

  const handleNextClick = useCallback(() => {
    setItemOffset((o) => Math.min(o + cardsPerPage, totalCards - cardsPerPage));
    setLastSlideItem((o) => Math.min(o + cardsPerPage, totalCards));
  }, [cardsPerPage, totalCards]);

  const handlePreviousClick = useCallback(() => {
    setItemOffset((o) => Math.max(o - cardsPerPage, 0));
    setLastSlideItem((o) => Math.max(o - cardsPerPage, cardsPerPage));
  }, [cardsPerPage]);

  useEffect(() => {
    setLastSlideItem(cardsPerPage);
    setItemOffset(0);
  }, [cardsPerPage]);

  return useMemo(() => {
    return {
      menuCarouselProps: {
        handleNextClick,
        handlePreviousClick,
        itemOffset,
        lastItemSlide,
        cardsPerPage,
      },
      menuItemProps: { itemOffset, lastItemSlide },
    };
  }, [handleNextClick, handlePreviousClick, itemOffset, lastItemSlide, cardsPerPage]);
};
